import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_URL } from "../../components/01_config/api";

// const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

const COLUMNS = [
  "grammar",
  "readingTexts",
  "readingSigns",
  "listening",
  "speakingDialogs",
  "speakingStories",
];

// ==========================
// THUNKS
// ==========================

// Load progress chart (student OR teacher-selected student)
export const fetchProgressChart = createAsyncThunk(
  "progressChart/fetchProgressChart",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.auth.token;
    const role = state.auth?.user?.role;
    const selectedStudentId = state.teacher?.selectedStudentId;

    if (!token) return thunkAPI.rejectWithValue("Not authenticated");

    const url =
      role === "TEACHER" || role === "ADMIN"
        ? selectedStudentId
          ? `${API_URL}/teacher/students/${selectedStudentId}/progress-chart`
          : null
        : `${API_URL}/me/progress-chart`;

    if (!url) {
      return thunkAPI.rejectWithValue("No student selected");
    }

    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        return thunkAPI.rejectWithValue(body.error || "Failed to load progress chart");
      }

      return body; // array of ProgressChartCell rows
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || "Network error");
    }
  }
);

// Teacher toggles a cell
export const toggleProgressCell = createAsyncThunk(
  "progressChart/toggleProgressCell",
  async ({ lessonNumber, columnKey, status }, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.auth.token;
    const role = state.auth?.user?.role;
    const selectedStudentId = state.teacher?.selectedStudentId;

    if (!token) return thunkAPI.rejectWithValue("Not authenticated");
    if (role !== "TEACHER" && role !== "ADMIN") {
      return thunkAPI.rejectWithValue("Forbidden");
    }
    if (!selectedStudentId)
      return thunkAPI.rejectWithValue("No student selected");

    try {
      const res = await fetch(
        `${API_URL}/teacher/students/${selectedStudentId}/progress-chart`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ lessonNumber, columnKey, status }),
        }
      );

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        return thunkAPI.rejectWithValue(body.error || "Failed to update progress cell");
      }

      return body; // updated cell
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || "Network error");
    }
  }
);

// ==========================
// SLICE
// ==========================

const progressChartSlice = createSlice({
  name: "progressChart",
  initialState: {
    cells: [],       // DB rows
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchProgressChart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProgressChart.fulfilled, (state, action) => {
        state.loading = false;
        state.cells = action.payload;
      })
      .addCase(fetchProgressChart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load progress chart";
      })

      // toggle
      .addCase(toggleProgressCell.fulfilled, (state, action) => {
        const updated = action.payload;

        const idx = state.cells.findIndex(
          (c) =>
            c.userId === updated.userId &&
            c.lessonNumber === updated.lessonNumber &&
            c.columnKey === updated.columnKey
        );

        if (idx >= 0) {
          state.cells[idx].status = updated.status;
        } else {
          state.cells.push(updated);
        }
      })
      .addCase(toggleProgressCell.rejected, (state, action) => {
        state.error = action.payload || "Failed to update progress cell";
      });
  },
});

export default progressChartSlice.reducer;
